import { useCallback, useEffect, useRef, useState } from "react";

const REVEAL_WIDTH = 160;
const SNAP_THRESHOLD = 40;
const AXIS_THRESHOLD = 6;

export function useRowSwipe(): {
    contentRef: React.RefObject<HTMLDivElement>;
    isRevealed: boolean;
    close: () => void;
} {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isRevealed, setIsRevealed] = useState(false);

    const startXRef = useRef(0);
    const startYRef = useRef(0);
    // Position of the element at the start of the current touch gesture.
    const startOffsetRef = useRef(0);
    // Position being tracked during the gesture.
    const currentOffsetRef = useRef(0);
    const axisLockedRef = useRef<"horizontal" | "vertical" | null>(null);
    const isRevealedRef = useRef(false);

    isRevealedRef.current = isRevealed;

    const snap = useCallback((open: boolean) => {
        const el = contentRef.current;
        if (!el) {
            return;
        }
        const target = open ? -REVEAL_WIDTH : 0;
        el.style.transition = "transform 0.2s ease";
        el.style.transform = `translateX(${target}px)`;
        currentOffsetRef.current = target;
        setIsRevealed(open);
    }, []);

    const close = useCallback(() => snap(false), [snap]);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) {
            return;
        }

        const handleTouchStart = (e: TouchEvent) => {
            startXRef.current = e.touches[0].clientX;
            startYRef.current = e.touches[0].clientY;
            axisLockedRef.current = null;
            el.style.transition = "none";
            // Read actual visual position — may be mid-animation if a snap is in progress.
            const transform = getComputedStyle(el).transform;
            const visualOffset = transform !== "none" ? new DOMMatrix(transform).m41 : 0;
            startOffsetRef.current = visualOffset;
            currentOffsetRef.current = visualOffset;
        };

        const handleTouchMove = (e: TouchEvent) => {
            const dx = e.touches[0].clientX - startXRef.current;
            const dy = e.touches[0].clientY - startYRef.current;

            if (!axisLockedRef.current) {
                if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD) {
                    return;
                }
                axisLockedRef.current = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
            }

            if (axisLockedRef.current !== "horizontal") {
                return;
            }

            e.stopPropagation();
            e.preventDefault();

            const raw = startOffsetRef.current + dx;
            const clamped = Math.max(-REVEAL_WIDTH, Math.min(0, raw));
            currentOffsetRef.current = clamped;
            el.style.transform = `translateX(${clamped}px)`;
        };

        const handleTouchEnd = () => {
            if (axisLockedRef.current !== "horizontal") {
                return;
            }
            const displacement = currentOffsetRef.current - startOffsetRef.current;
            if (displacement <= -SNAP_THRESHOLD) {
                snap(true);
            } else if (displacement >= SNAP_THRESHOLD) {
                snap(false);
            } else {
                // Not moved far enough — snap back to where the gesture started.
                snap(isRevealedRef.current);
            }
        };

        el.addEventListener("touchstart", handleTouchStart, { passive: true });
        el.addEventListener("touchmove", handleTouchMove, { passive: false });
        el.addEventListener("touchend", handleTouchEnd, { passive: true });
        el.addEventListener("touchcancel", handleTouchEnd, { passive: true });

        return () => {
            el.removeEventListener("touchstart", handleTouchStart);
            el.removeEventListener("touchmove", handleTouchMove);
            el.removeEventListener("touchend", handleTouchEnd);
            el.removeEventListener("touchcancel", handleTouchEnd);
        };
    }, [snap]);

    return { contentRef, isRevealed, close };
}
